import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Toast } from '../../../components/Common/Toast';
import DummyPic from '../../../components/Image holder Icon.png';
import { setStoryData } from '../../../store/storySlice';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import heic2any from 'heic2any';
import { StoryCharacterAgeUnitEnum, StoryCharacterEnum } from '../../../interfaces/character.interface';
import { useStudentVPCStatus } from '../../../hooks/useStudentVPCStatus';
import Loader from '../../../components/Common/Loader';
import { setUploadImageExamplePopup } from '../../../store/appSlice';

const formats = {
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
  'image/heic': ['.heic'],
};

interface UploaderProps {
  onFileChange: (file: File) => void;
  setPreviewImage: (image: string) => void;
  previewImage?: string;
  selectedImage?: string;
  disabled?: boolean;
  currentPortrait?: string;
  setShouldCoverPortrait?: (shouldCover: boolean) => void;
  title?: string;
  warningType?: 'default' | 'small';
}

const Uploader = ({
  onFileChange,
  setPreviewImage,
  previewImage,
  selectedImage,
  disabled = false,
  currentPortrait,
  setShouldCoverPortrait,
  title = 'Upload Headshot of Main Character',
  warningType = 'default',
}: UploaderProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const storyData = useSelector((state: RootState) => state.story.storyData);
  const uploadImageExamplePopup = useSelector((state: RootState) => state.app.uploadImageExamplePopup);
  const dispatch: AppDispatch = useDispatch();
  const [showedUploadImageExamplePopup, setShowedUploadImageExamplePopup] = useState<boolean>(false);
  const { vpcRequired, vpcComplete, loading, teacherState, isVPCBlocked } = useStudentVPCStatus();

  useEffect(() => {
    if (uploadImageExamplePopup === 'submit') {
      setShowedUploadImageExamplePopup(true);
    }
    return () => {
      setShowedUploadImageExamplePopup(false);
    };
  }, [uploadImageExamplePopup]);

  const handleFileUpload = async (file: File) => {
    if (!file || !onFileChange || typeof onFileChange !== 'function') {
      return;
    }

    const isHEIC = file.type === 'image/heic' || file.type === 'image/heif';

    try {
      let fileToProcess = file;

      // Convert HEIC/HEIF files to a supported format (e.g., JPEG)
      if (isHEIC) {
        const convertedBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 1 });
        fileToProcess = convertedBlob as File;
        const convertedFile = new File(
          Array.isArray(convertedBlob) ? convertedBlob : [convertedBlob],
          file.name.replace(/\.heic$/i, '.jpeg'),
          {
            type: 'image/jpeg',
          },
        );
        onFileChange(convertedFile);
      } else {
        onFileChange(file);
      }

      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();

        img.onload = () => {
          const width = img.width;
          const height = img.height;

          setShouldCoverPortrait && setShouldCoverPortrait(width < 300 || height < 300 || width > 500 || height > 500);

          dispatch(
            setStoryData({
              ...storyData,
              uploadedImage: event?.target?.result! as any,
              newRefImageUploaded: true,
              regeneratedNewCharacter: false,
              age: '',
              ageUnit: StoryCharacterAgeUnitEnum.year,
              gender: '',
              animalType: undefined,
              characterType: StoryCharacterEnum.human,
              mics: '',
            }),
          );

          setPreviewImage(event?.target?.result! as string);
        };

        // Handle potential image loading errors gracefully
        img.onerror = (error) => {
          Toast('Error: Could not load the selected image.', true);
        };

        img.src = event?.target?.result as string; // Set the image source
      };

      reader.onerror = (error) => {
        Toast('Error: An error occurred while processing the file.', true);
      };

      reader.readAsDataURL(fileToProcess); // Read the processed file as Data URL
    } catch (error) {
      Toast('Error: Could not process the selected file.', true);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: any) => {
      handleFileUpload(acceptedFiles[0]);
      if (fileInputRef.current) {
        (fileInputRef.current as HTMLInputElement).value = '';
      }
    },
    [onFileChange],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: formats,
    noKeyboard: true,
  });

  useEffect(() => {
    if (!(selectedImage && previewImage)) {
      setPreviewImage(DummyPic);

      dispatch(
        setStoryData({
          ...storyData,
          isDummyPic: true,
        }),
      );
    }
  }, [selectedImage, previewImage]);

  useEffect(() => {
    if (currentPortrait) setPreviewImage(currentPortrait);
  }, [currentPortrait]);

  // Show VPC message if required for students
  if (isVPCBlocked()) {
    return (
      <div className="flex flex-col items-center justify-center w-full bg-gray-50 border-gray-300 border-2 rounded-lg border-dashed">
        <div
          className={`text-center max-w-md ${warningType === 'small' ? 'flex items-center' : 'p-4'}`}
          title={'Please ask your teacher to complete the parental consent form before you can upload photos.'}
        >
          <div className={`${warningType === 'small' ? 'mb-0' : 'mb-4'}`}>
            <svg className="mx-auto h-12 w-12 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>

          <h3 className={`${warningType === 'small' ? 'text-sm' : 'text-lg'} font-semibold text-gray-900`}>
            Photo Upload Not Available
          </h3>

          {warningType !== 'small' && (
            <p className="text-gray-600 mb-4 mt-2">
              Your teacher is located in <strong>{teacherState || 'Illinois'}</strong>, which requires Verified Parental
              Consent for photo uploads.
            </p>
          )}

          {warningType !== 'small' && (
            <p className="text-sm text-gray-500">
              Please ask your teacher to complete the parental consent form before you can upload photos.
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={`flex  gap-4 items-center justify-around w-full bg-gray-50  border-gray-300 border-2 rounded-lg  ${
        selectedImage || previewImage ? 'h-[4.5rem] rounded-[1.5rem] border-solid' : 'h-[20rem] border-dashed'
      } `}
    >
      {loading && (
        <div className="flex w-full h-full items-center justify-center">
          <Loader loader={loading} dimentions={'w-7 h-7'} />
        </div>
      )}

      {!loading && (
        <div
          onClick={(event) => {
            if (showedUploadImageExamplePopup) return;
            event.preventDefault();
            dispatch(setUploadImageExamplePopup('open'));
          }}
        >
          <label
            htmlFor="dropzone-file"
            className={`flex flex-col items-center justify-center w-full ${disabled ? '' : 'cursor-pointer'} bg-gray-50`}
          >
            <div
              className={`flex flex-col items-center justify-center ${selectedImage || previewImage ? '' : 'pt-5 pb-6'}`}
            >
              {!(selectedImage || previewImage) ? (
                <svg
                  className="w-10 h-10 mb-3 text-gray-400 "
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  ></path>
                </svg>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faCloudArrowUp}
                    className={` ${disabled ? 'text-[#c1c1c1]' : 'cursor-pointer text-storygreen'}  text-2xl `}
                    title="Upload Image"
                    onClick={() => {}}
                  />
                  <p className="text-sm text-gray-500 dark:text-gray-400">Optional</p>
                </>
              )}

              {!(selectedImage || previewImage) && (
                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400 text-center">
                  <span className="font-semibold">
                    {title}
                    <br />
                    or <br />
                    Drag & Drop Photo Here
                  </span>
                </p>
              )}
            </div>
            <input
              disabled={disabled}
              {...getInputProps()}
              id="dropzone-file"
              type="file"
              className="hidden"
              ref={fileInputRef}
            />
          </label>
        </div>
      )}
    </div>
  );
};

export default Uploader;
